import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

import { toast } from "sonner";
import { User, IdCard, MapPin, CircleDollarSign, Megaphone, FileText, Hash } from "lucide-react";

export default function ReservationEditDialog({ reservation, onClose, onSaved }) {
  // Verificación de rol para seguridad en la UI
  const isAdmin = localStorage.getItem("role") === "admin";
  const { user, ready, logout } = useAuth();
  const userRole = user?.role || "vendedor";

  const [formData, setFormData] = useState({
    id_client: "",
    notes: "",
    cliente: {
      nombre: "", apellido_paterno: "", apellido_materno: "", age: 0, fecha_nacimiento: "", telefono: "", email: ""
    },
    datos_cliente: {
      estatus_conyugal: "", nacionalidad: "", beneficiario: "", tipo_casa: "", dependientes: 0, occupacion: "", lugar_trabajo: "", antiguedad_trabajo: ""
    },
    direccion: {
      calle: "", numero: "", colonia: "", codigo_postal: "", municipio: "", estado: ""
    },
    contributions: {
      total_contribution: 0, total_contribution_text: "", reservation_payment: 0, initial_contribution: 0, monthly_contribution: 0, monthly_amount: 0, first_monthly_payment_day: "", notes: ""
    },
    promoter: {
      name: "", office_address: "", office_phone: "", office_email: ""
    }
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (reservation) {
      setFormData({
        id_client: reservation.id_client || "",
        notes: reservation.notes || "",
        cliente: {
          nombre: reservation.cliente?.nombre || "",
          apellido_paterno: reservation.cliente?.apellido_paterno || "",
          apellido_materno: reservation.cliente?.apellido_materno || "",
          age: reservation.cliente?.age || 0,
          fecha_nacimiento: reservation.cliente?.fecha_nacimiento || "",
          telefono: reservation.cliente?.telefono || "",
          email: reservation.cliente?.email || ""
        },
        datos_cliente: {
          estatus_conyugal: reservation.datos_cliente?.estatus_conyugal || "",
          nacionalidad: reservation.datos_cliente?.nacionalidad || "",
          beneficiario: reservation.datos_cliente?.beneficiario || "",
          tipo_casa: reservation.datos_cliente?.tipo_casa || "",
          dependientes: reservation.datos_cliente?.dependientes || 0,
          occupacion: reservation.datos_cliente?.occupacion || "",
          lugar_trabajo: reservation.datos_cliente?.lugar_trabajo || "",
          antiguedad_trabajo: reservation.datos_cliente?.antiguedad_trabajo || ""
        },
        direccion: {
          calle: reservation.direccion?.calle || "",
          numero: reservation.direccion?.numero || "",
          colonia: reservation.direccion?.colonia || "",
          codigo_postal: reservation.direccion?.codigo_postal || "",
          municipio: reservation.direccion?.municipio || "",
          estado: reservation.direccion?.estado || ""
        },
        contributions: {
          total_contribution: reservation.contributions?.total_contribution || 0,
          total_contribution_text: reservation.contributions?.total_contribution_text || "",
          reservation_payment: reservation.contributions?.reservation_payment || 0,
          initial_contribution: reservation.contributions?.initial_contribution || 0,
          monthly_contribution: reservation.contributions?.monthly_contribution || 0,
          monthly_amount: reservation.contributions?.monthly_amount || 0,
          first_monthly_payment_day: reservation.contributions?.first_monthly_payment_day || "",
          notes: reservation.contributions?.notes || ""
        },
        promoter: {
          name: reservation.promoter?.name || "",
          office_address: reservation.promoter?.office_address || "",
          office_phone: reservation.promoter?.office_phone || "",
          office_email: reservation.promoter?.office_email || ""
        }
      });
    }
  }, [reservation]);

  if (!reservation) return null;

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const targetId = reservation.reservation_id || reservation.id;
      await api.patch(`/reservations/${targetId}`, formData);
      toast.success("Reserva actualizada correctamente");
      onSaved();
    } catch (err) {
      toast.error("Error al guardar", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!reservation} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reservation-edit-dialog" className="max-w-5xl p-0 overflow-hidden rounded-2xl bg-slate-50">
        
        <DialogHeader className="p-6 pb-4 border-b border-slate-200 bg-white">
          <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2 text-slate-900">
            Editar Reserva <span className="text-blue-600">#{reservation.reservation_id}</span>
          </DialogTitle>
          <p className="text-slate-500 text-sm mt-1">Actualiza los datos del cliente, pagos y promotor asociado.</p>
        </DialogHeader>

        <form onSubmit={submit} className="overflow-y-auto max-h-[75vh]">
          <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* ================= COLUMNA IZQUIERDA (CLIENTE) ================= */}
            <div className="space-y-6">
              
              {/* Sección ID Cliente */}
              <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm hidden ">
                <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 mb-4">
                  <Hash size={16} className="text-slate-400" /> Identificador
                </h4>
                <div className="space-y-1.5">
                  <Label htmlFor="id_client" className="text-xs font-semibold text-slate-500 uppercase tracking-wider">ID Cliente en Sistema</Label>
                  <Input id="id_client" type="number" className="h-10 font-mono bg-slate-50 max-w-[200px]" value={formData.id_client} onChange={(e) => setFormData({ ...formData, id_client: Number(e.target.value) })} />
                </div>
              </div>

              {/* Sección Cliente */}
              <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
                <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                  <User size={16} className="text-blue-500" /> Información Principal
                </h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5"><Label className="text-slate-600">Nombre</Label><Input className="h-10" value={formData.cliente.nombre} onChange={(e) => handleNestedChange("cliente", "nombre", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label className="text-slate-600">Ap. Paterno</Label><Input className="h-10" value={formData.cliente.apellido_paterno} onChange={(e) => handleNestedChange("cliente", "apellido_paterno", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label className="text-slate-600">Ap. Materno</Label><Input className="h-10" value={formData.cliente.apellido_materno} onChange={(e) => handleNestedChange("cliente", "apellido_materno", e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5"><Label className="text-slate-600">Edad</Label><Input type="number" className="h-10" value={formData.cliente.age || ""} onChange={(e) => handleNestedChange("cliente", "age", Number(e.target.value))} /></div>
                    <div className="space-y-1.5"><Label className="text-slate-600">F. Nacimiento</Label><Input type="date" className="h-10" value={formData.cliente.fecha_nacimiento} onChange={(e) => handleNestedChange("cliente", "fecha_nacimiento", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label className="text-slate-600">Teléfono</Label><Input className="h-10" value={formData.cliente.telefono} onChange={(e) => handleNestedChange("cliente", "telefono", e.target.value)} /></div>
                  </div>
                  <div className="space-y-1.5"><Label className="text-slate-600">Correo Electrónico</Label><Input type="email" className="h-10" value={formData.cliente.email} onChange={(e) => handleNestedChange("cliente", "email", e.target.value)} /></div>
                </div>
              </div>

              {/* Sección Datos Personales */}
              <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
                <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                  <IdCard size={16} className="text-purple-500" /> Perfil Demográfico y Laboral
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                  <div className="space-y-1.5"><Label className="text-slate-600">Estado Civil</Label><Input className="h-10" value={formData.datos_cliente.estatus_conyugal} onChange={(e) => handleNestedChange("datos_cliente", "estatus_conyugal", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label className="text-slate-600">Nacionalidad</Label><Input className="h-10" value={formData.datos_cliente.nacionalidad} onChange={(e) => handleNestedChange("datos_cliente", "nacionalidad", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label className="text-slate-600">Beneficiario</Label><Input className="h-10" value={formData.datos_cliente.beneficiario} onChange={(e) => handleNestedChange("datos_cliente", "beneficiario", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label className="text-slate-600">Tipo de Casa</Label><Input className="h-10" value={formData.datos_cliente.tipo_casa} onChange={(e) => handleNestedChange("datos_cliente", "tipo_casa", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label className="text-slate-600">Dependientes</Label><Input type="number" className="h-10" value={formData.datos_cliente.dependientes || ""} onChange={(e) => handleNestedChange("datos_cliente", "dependientes", Number(e.target.value))} /></div>
                  <div className="space-y-1.5"><Label className="text-slate-600">Ocupación</Label><Input className="h-10" value={formData.datos_cliente.occupacion} onChange={(e) => handleNestedChange("datos_cliente", "occupacion", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label className="text-slate-600">Empresa / Trabajo</Label><Input className="h-10" value={formData.datos_cliente.lugar_trabajo} onChange={(e) => handleNestedChange("datos_cliente", "lugar_trabajo", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label className="text-slate-600">Antigüedad Laboral</Label><Input className="h-10" value={formData.datos_cliente.antiguedad_trabajo} onChange={(e) => handleNestedChange("datos_cliente", "antiguedad_trabajo", e.target.value)} /></div>
                </div>
              </div>

              {/* Sección Dirección */}
              <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
                <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                  <MapPin size={16} className="text-emerald-500" /> Dirección de Residencia
                </h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-4 gap-3">
                    <div className="col-span-3 space-y-1.5"><Label className="text-slate-600">Calle</Label><Input className="h-10" value={formData.direccion.calle} onChange={(e) => handleNestedChange("direccion", "calle", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label className="text-slate-600">Número</Label><Input className="h-10" value={formData.direccion.numero} onChange={(e) => handleNestedChange("direccion", "numero", e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-slate-600">Colonia</Label><Input className="h-10" value={formData.direccion.colonia} onChange={(e) => handleNestedChange("direccion", "colonia", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label className="text-slate-600">Código Postal</Label><Input className="h-10" value={formData.direccion.codigo_postal} onChange={(e) => handleNestedChange("direccion", "codigo_postal", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label className="text-slate-600">Municipio</Label><Input className="h-10" value={formData.direccion.municipio} onChange={(e) => handleNestedChange("direccion", "municipio", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label className="text-slate-600">Estado</Label><Input className="h-10" value={formData.direccion.estado} onChange={(e) => handleNestedChange("direccion", "estado", e.target.value)} /></div>
                  </div>
                </div>
              </div>

            </div>

            {/* ================= COLUMNA DERECHA (FINANZAS Y EXTRAS) ================= */}
            <div className="space-y-6">
              
              {/* Sección Contribuciones (SOLO ADMIN) */}
              {userRole === 'admin' && (
                <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
                  <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                    <CircleDollarSign size={16} className="text-amber-500" /> Esquema de Pagos
                  </h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5"><Label className="text-slate-600">Valor Total Lote</Label><Input type="number" className="h-10 font-mono" value={formData.contributions.total_contribution || ""} onChange={(e) => handleNestedChange("contributions", "total_contribution", Number(e.target.value))} /></div>
                      <div className="space-y-1.5"><Label className="text-slate-600">Pago de Apartado</Label><Input type="number" className="h-10 font-mono" value={formData.contributions.reservation_payment || ""} onChange={(e) => handleNestedChange("contributions", "reservation_payment", Number(e.target.value))} /></div>
                      <div className="space-y-1.5"><Label className="text-slate-600">Enganche Inicial</Label><Input type="number" className="h-10 font-mono" value={formData.contributions.initial_contribution || ""} onChange={(e) => handleNestedChange("contributions", "initial_contribution", Number(e.target.value))} /></div>
                      <div className="space-y-1.5"><Label className="text-slate-600">Día Primer Pago</Label><Input type="number" placeholder="Ej. 15" className="h-10" value={formData.contributions.first_monthly_payment_day} onChange={(e) => handleNestedChange("contributions", "first_monthly_payment_day", e.target.value)} /></div>
                    </div>
                    
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-100 grid grid-cols-2 gap-4">
                      <div className="space-y-1.5"><Label className="text-slate-600">Plazo (Meses)</Label><Input type="number" className="h-10" value={formData.contributions.monthly_contribution || ""} onChange={(e) => handleNestedChange("contributions", "monthly_contribution", Number(e.target.value))} /></div>
                      <div className="space-y-1.5"><Label className="text-slate-600">Monto Mensualidad</Label><Input type="number" className="h-10 font-mono" value={formData.contributions.monthly_amount || ""} onChange={(e) => handleNestedChange("contributions", "monthly_amount", Number(e.target.value))} /></div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-slate-600">Valor Total (Texto)</Label>
                      <Input className="h-10" placeholder="Ej. Cien mil pesos 00/100 M.N." value={formData.contributions.total_contribution_text} onChange={(e) => handleNestedChange("contributions", "total_contribution_text", e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-slate-600">Notas sobre Pagos</Label>
                      <Textarea rows={2} className="resize-none" value={formData.contributions.notes} onChange={(e) => handleNestedChange("contributions", "notes", e.target.value)} />
                    </div>
                  </div>
                </div>
              )}

              {/* Sección Promotor */}
              <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
                <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                  <Megaphone size={16} className="text-indigo-500" /> Datos del Promotor
                </h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-slate-600">Nombre</Label><Input className="h-10" value={formData.promoter.name} onChange={(e) => handleNestedChange("promoter", "name", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label className="text-slate-600">Teléfono</Label><Input className="h-10" value={formData.promoter.office_phone} onChange={(e) => handleNestedChange("promoter", "office_phone", e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-slate-600">Email</Label><Input type="email" className="h-10" value={formData.promoter.office_email} onChange={(e) => handleNestedChange("promoter", "office_email", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label className="text-slate-600">Oficina</Label><Input className="h-10" value={formData.promoter.office_address} onChange={(e) => handleNestedChange("promoter", "office_address", e.target.value)} /></div>
                  </div>
                </div>
              </div>

              {/* Notas Generales */}
              <div className="p-5 bg-amber-50/50 border border-amber-100 rounded-xl shadow-sm">
                <h4 className="font-bold text-sm flex items-center gap-2 text-amber-800 border-b border-amber-200/60 pb-2 mb-4">
                  <FileText size={16} className="text-amber-500" /> Observaciones de la Reserva
                </h4>
                <div className="space-y-1.5">
                  <Textarea id="edit_notes" rows={4} className="bg-white resize-none" placeholder="Escribe aquí notas internas, acuerdos especiales o detalles a recordar..." value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                </div>
              </div>

            </div>
          </div>
          
          <DialogFooter className="p-6 bg-white border-t border-slate-200 gap-3">
            <Button type="button" variant="outline" className="h-11 px-6 border-slate-300 hover:bg-slate-50" onClick={onClose}>
              Cancelar Edición
            </Button>
            <Button type="submit" className="h-11 px-8 bg-slate-900 hover:bg-slate-800 text-white shadow-lg shadow-slate-900/10" disabled={saving}>
              {saving ? "Guardando cambios…" : "Actualizar Reserva"}
            </Button>
          </DialogFooter>
        </form>

      </DialogContent>
    </Dialog>
  );
}